import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Chapter, Reveal } from "@/components/motion";
import { IMAGES } from "@/data/site";

const POINTS = [
  "Appropriate worker selection and safety awareness",
  "Training, competency and compliance documentation",
  "Site-specific requirements and communication",
  "Incident reporting, response and ongoing monitoring",
];

export default function SafetySection() {
  return (
    <section className="relative overflow-hidden bg-navy py-28 lg:py-36" data-testid="safety-section">
      <div className="absolute inset-0 bg-blueprint-dark opacity-30" />
      <div className="relative z-10 mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-16 lg:grid-cols-2">
          <div>
            <Chapter index="05" label="Safety & Compliance" dark />
            <Reveal delay={0.1}>
              <h2 className="mt-8 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl">
                Safety Is<br /><span className="text-gold">Non-Negotiable</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base leading-relaxed text-white/70">
                Construction is a high-responsibility environment. Formcore maintains robust safety processes and
                ensures personnel understand and comply with the requirements applicable to their work — treating
                safety not simply as a compliance obligation, but as an integral part of professional performance.
              </p>
            </Reveal>
            <ul className="mt-8 space-y-4">
              {POINTS.map((point, i) => (
                <Reveal key={point} delay={0.25 + i * 0.07} y={16}>
                  <li className="flex items-start gap-3 text-sm text-white/80">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
                    {point}
                  </li>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.55}>
              <p className="mt-10 font-heading text-xl font-bold text-white">
                Safe people. Safe workplaces. <span className="text-gold">Better outcomes.</span>
              </p>
              <Link
                to="/safety-compliance"
                data-testid="safety-approach-link"
                className="mt-6 inline-flex items-center gap-2 font-heading text-sm font-bold uppercase tracking-[0.15em] text-gold transition-colors hover:text-white"
              >
                Our safety approach
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="relative">
            <div className="absolute -left-5 -top-5 hidden h-full w-full border-2 border-gold/40 md:block" />
            <img
              src={IMAGES.safety}
              alt="Supervisor and worker reviewing site safety documentation"
              className="clip-frame relative h-[340px] w-full object-cover md:h-[520px]"
            />
            <div className="absolute bottom-6 left-6 border-l-4 border-gold bg-navy/90 px-5 py-4 backdrop-blur">
              <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/60">Safety-first culture</p>
              <p className="mt-1 font-heading text-sm font-bold text-white">Shared responsibility on every site</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
